import { useParams, useNavigate } from "react-router-dom";
import products from "../../mocks/product.json";
import {
  Box,
  Button,
  Typography,
  Stack,
  Pagination,
  PaginationItem,
  IconButton,
  TextField,
  InputAdornment,
  Divider,
  Tooltip,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Nav from "../../components/Nav";
import Breadcrumb from "../../components/Breadcrumb";
import BannerImage from "../../assets/images/store-banner.png";
import { useState, useMemo } from "react";
import styles from "./index.module.css";

const pageSize = 5;

const CategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [showFilter, setShowFilter] = useState(false);

  const categoryProduct = products.find((p) => p.id === categoryId);
  const flattenProducts = categoryProduct ? categoryProduct.list : [];

  const paginatedProducts = useMemo(() => {
    const acpage = page - 1;
    const offset = acpage * pageSize;
    return flattenProducts.slice(offset, offset + pageSize);
  }, [page, flattenProducts]);

  return (
    <Stack className={styles.landingPage} component="main" direction="column">
      <Stack className={styles.mainSection}>
        <Box className={styles.heroBanner}>
          <img src={BannerImage} alt="hero-banner" />
        </Box>
        <Box className={styles.content}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <TextField
              variant="outlined"
              size="small"
              sx={{
                height: "32px",
                "& .MuiOutlinedInput-root": {
                  height: "32px",
                  backgroundColor: "white",
                  borderRadius: 0,
                },
                "& .MuiInputBase-root": {
                  paddingRight: "2px",
                },
                "& .MuiInputBase-input": {
                  paddingRight: "32px",
                },
              }}
              placeholder="Search"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Stack color="primary">
                      <SearchIcon
                        sx={{
                          backgroundColor: "#0a9a73",
                          color: "white",
                          padding: "2px",
                        }}
                      />
                    </Stack>
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  background: "white",
                  height: "30px",
                  borderRadius: "4px",
                  padding: "8px",
                  boxSizing: "border-box",
                }}
              >
                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                  <Typography sx={{ lineHeight: 1 }} variant="overline">
                    EN
                  </Typography>
                  <ExpandMoreIcon sx={{ stroke: 0.5 }} />
                </Stack>
                <Divider sx={{ mx: "4px" }} orientation="vertical" />
                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                  <Typography sx={{ lineHeight: 1 }} variant="overline">
                    USD
                  </Typography>
                  <ExpandMoreIcon />
                </Stack>
              </Stack>
              <Button variant="contained" size="small" sx={{ display: { xs: "none", sm: "block" } }}>
                Login | Pay Bill
              </Button>
              <Tooltip title="Login | Pay Bill">
                <IconButton
                  variant="contained"
                  size="large"
                  sx={{ color: "white", display: { xs: "flex", sm: "none" } }}
                >
                  <AccountCircleIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Nav />
          <Breadcrumb
            links={[
              { title: "Home", url: "/" },
              { title: "Medical Supplies", url: "/medicalsupplies" },
              { title: categoryProduct?.title || "Category" },
            ]}
          />
        </Box>
      </Stack>
      <Box sx={{ background: "white" }}>
        <Box className={styles.content}>
          <Stack direction="row" sx={{ alignItems: "center",justifyContent:"space-between",my:2 }}>
            <IconButton
              onClick={() => setShowFilter(true)}
              sx={{ display: { xs: "block", md: "none" } }}
            >
              {/* <FilterAltIcon /> */}
            </IconButton>
            <Typography variant="h4">
              {categoryProduct?.title || "Medical Supplies"}
            </Typography>
            <Button
            variant="outlined"
            onClick={() => navigate("/medicalsupplies")}
            sx={{ mb: 3 }}
          >
            Back
          </Button>
          </Stack>
          <Stack spacing={2}>
            {paginatedProducts.map((p) => (
              <Stack
                key={p.id}
                onClick={() => navigate(`/medicalsupplies/${categoryId}/${p.id}`)}
                direction={{ xs: "column", sm: "row" }}
                spacing={3}
                sx={{
                  padding: "22px",
                  boxShadow: "0px 3px 31px #4C31DC1A",
                  boxSizing: "border-box",
                      alignItems: "top",
                      cursor: "pointer",
                      "&:hover": {
                        boxShadow: "0px 0px 2px #20bfa9",
                        pointer: "cursor !important",
                      },
                }}
              >
                <Box sx={{ minWidth: "230px", width: "230px", height: "230px" }}>
                  <img src={p.img} alt={p.title} width="100%" height="100%" style={{border:"1px solid lightgrey"}} />
                </Box>
                <Stack>
                  <Typography variant="h5">{p.title}</Typography>
                  <Typography variant="body1" sx={{mt:1}}>{p.description}</Typography>
                  {p.subcategories && (
                    <Stack spacing={1} sx={{ mt: 2, pl: 1 }}>
                      {p.subcategories.map((sub, index) => (
                        <Box
                          key={index}
                          sx={{ color: "#0a9a73", cursor: "pointer", "&:hover": {textDecoration: "underline"} }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/medicalsupplies/${categoryId}/${p.id}/${sub.id}`
                            );
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            component="li"
                            sx={{ fontWeight: 400 }}
                          >
                            {sub.title}
                          </Typography>
                          {/* <Typography variant="body2">{sub.description}</Typography> */}
                        </Box>
                      ))}
                    </Stack>
                  )}
                    {p.link && (
                        <Button
                            sx={{ mt: 2, width: "fit-content", textTransform: "none" }}
                            color="primary"
                            onClick={() => window.open(p.link, "_blank", "noopener,noreferrer")}
                            size="small"
                            variant="contained"
                        >
                            See more details
                        </Button>
                    )}
                </Stack>
              </Stack>
            ))}
          </Stack>
          <Stack sx={{ alignItems: "center", mt: "auto !important", padding: "1rem" }}>
            <Pagination
              color="primary"
              count={Math.ceil(flattenProducts.length / 5)}
              page={page}
              onChange={(_, nextPage) => setPage(nextPage)}
              renderItem={(item) => (
                <PaginationItem
                  slots={{
                    previous: ArrowBackIcon,
                    next: ArrowForwardIcon,
                  }}
                  {...item}
                />
              )}
            />
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
};

export default CategoryPage;